import { List, useTable, EditButton, ShowButton, CreateButton } from '@refinedev/antd';
import { Table, Tag, Space, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import { BidiText, MonoCode, formatDateBidi } from '@lebinatikra/rtl-utils';

interface UserRow {
  id: string;
  email: string | null;
  phone: string | null;
  default_locale: string;
  created_at: string;
  roles?: Array<{ id: string; role: string; organization_id: string; revoked_at: string | null }>;
}

export function UserList() {
  const { t } = useTranslation();
  const { tableProps, filters, setFilters } = useTable<UserRow>({
    resource: 'users',
    pagination: { pageSize: 25 },
    // Default per Story 3.10 AC8: active roles only.
    filters: { initial: [{ field: 'revoked', operator: 'eq', value: 'false' }] },
  });

  const revokedFilter = filters.find((f) => 'field' in f && f.field === 'revoked');
  const revokedValue =
    revokedFilter && 'value' in revokedFilter ? String(revokedFilter.value) : 'false';

  return (
    <List
      headerButtons={() => (
        <Space>
          <Select
            aria-label={t('filters.revoked')}
            value={revokedValue}
            style={{ minWidth: 160 }}
            onChange={(value: string) =>
              setFilters([{ field: 'revoked', operator: 'eq', value }])
            }
            options={[
              { value: 'false', label: t('filters.revoked_active') },
              { value: 'true', label: t('filters.revoked_revoked') },
              { value: 'all', label: t('filters.revoked_all') },
            ]}
          />
          <CreateButton>{t('actions.create_invitation')}</CreateButton>
        </Space>
      )}
    >
      <Table {...tableProps} rowKey="id">
        <Table.Column<UserRow>
          dataIndex="email"
          title={t('auth.email')}
          render={(v) =>
            v ? (
              // rtl-utils:bidi-wrapped
              <BidiText dir="ltr">{v}</BidiText>
            ) : (
              <span style={{ opacity: 0.5 }}>—</span>
            )
          }
        />
        <Table.Column<UserRow>
          dataIndex="phone"
          title={t('fields.phone')}
          render={(v) =>
            v ? (
              // rtl-utils:bidi-wrapped
              <BidiText dir="ltr">{v}</BidiText>
            ) : (
              <span style={{ opacity: 0.5 }}>—</span>
            )
          }
        />
        <Table.Column<UserRow>
          dataIndex="default_locale"
          title={t('fields.default_locale')}
          render={(v) => <Tag>{v}</Tag>}
        />
        <Table.Column<UserRow>
          dataIndex="roles"
          title={t('fields.roles')}
          render={(roles: UserRow['roles']) => (
            <Space size={4} wrap>
              {(roles ?? []).map((r) => (
                <Tag key={r.id} color={r.revoked_at ? 'red' : 'blue'}>
                  {r.role}
                  {r.revoked_at ? ` (${t('notifications.revoked_access_tag')})` : ''}
                </Tag>
              ))}
            </Space>
          )}
        />
        <Table.Column<UserRow>
          dataIndex="created_at"
          title={t('common.created_at')}
          render={(v) => formatDateBidi(v)}
        />
        <Table.Column<UserRow>
          dataIndex="id"
          title={t('common.id')}
          render={(v) => (
            // rtl-utils:bidi-wrapped
            <MonoCode>{String(v).slice(0, 8)}</MonoCode>
          )}
        />
        <Table.Column<UserRow>
          title={t('common.actions')}
          render={(_, record) => (
            <>
              <ShowButton hideText size="small" recordItemId={record.id} />
              <EditButton hideText size="small" recordItemId={record.id} />
            </>
          )}
        />
      </Table>
    </List>
  );
}
